import { exponentialMovingAverage } from '../utils/smoothing';
import { KMH_TO_MS } from '../utils/speed';

export interface AccelerationResult {
  acceleration: number;           // m/s^2 (positive = accelerating, negative = braking/coast)
  effort: number;                 // Normalized 0.0 (cruise/idle) to 1.0 (aggressive throttle)
  isBraking: boolean;             // True if actively decelerating
  engineBrakingIntensity: number; // 0.0 to 1.0 intensity of engine braking sound
}

export class AccelerationProcessor {
  private lastSpeedKmh: number = 0;
  private lastTimestamp: number = 0;
  private smoothedAcceleration: number = 0;
  private smoothedEffort: number = 0;

  // Configuration thresholds
  private readonly maxEffortAccelMs2: number = 3.8;   // ~0-100 km/h in 7.3s
  private readonly brakingThresholdMs2: number = -0.3; // Below -0.3 m/s^2 counts as decel
  private readonly maxBrakingMs2: number = -4.0;       // Max engine braking reference

  /**
   * Calculate acceleration and effort from speed updates
   */
  public process(
    currentSpeedKmh: number,
    timestamp: number = performance.now()
  ): AccelerationResult {
    if (this.lastTimestamp === 0) {
      this.lastSpeedKmh = currentSpeedKmh;
      this.lastTimestamp = timestamp;
      return {
        acceleration: 0,
        effort: 0,
        isBraking: false,
        engineBrakingIntensity: 0,
      };
    }

    const dtSeconds = Math.max(0.01, (timestamp - this.lastTimestamp) / 1000);
    this.lastTimestamp = timestamp;

    // Calculate instantaneous acceleration in m/s^2
    const deltaSpeedKmh = currentSpeedKmh - this.lastSpeedKmh;
    this.lastSpeedKmh = currentSpeedKmh;

    const deltaSpeedMs = deltaSpeedKmh * KMH_TO_MS;
    const rawAcceleration = deltaSpeedMs / dtSeconds;

    // Apply EMA smoothing to prevent sound jitter from small GPS fluctuations
    this.smoothedAcceleration = exponentialMovingAverage(
      rawAcceleration,
      this.smoothedAcceleration,
      0.35
    );

    // Compute normalized effort (0.0 to 1.0)
    let rawEffort = 0;
    if (this.smoothedAcceleration > 0.1) {
      rawEffort = Math.min(1.0, (this.smoothedAcceleration - 0.1) / (this.maxEffortAccelMs2 - 0.1));
    }

    this.smoothedEffort = exponentialMovingAverage(
      rawEffort,
      this.smoothedEffort,
      0.4
    );

    // Determine braking / deceleration
    const isBraking = this.smoothedAcceleration < this.brakingThresholdMs2;
    let engineBrakingIntensity = 0;
    if (isBraking && currentSpeedKmh > 5.0) {
      const decel = Math.abs(this.smoothedAcceleration);
      engineBrakingIntensity = Math.min(1.0, Math.max(0.0, decel / Math.abs(this.maxBrakingMs2)));
    }

    return {
      acceleration: this.smoothedAcceleration,
      effort: Math.max(0, Math.min(1, this.smoothedEffort)),
      isBraking,
      engineBrakingIntensity,
    };
  }

  public setManualEffort(effort: number, acceleration: number = 0): AccelerationResult {
    this.smoothedEffort = Math.max(0, Math.min(1, effort));
    this.smoothedAcceleration = acceleration;
    const isBraking = acceleration < this.brakingThresholdMs2;
    return {
      acceleration: this.smoothedAcceleration,
      effort: this.smoothedEffort,
      isBraking,
      engineBrakingIntensity: isBraking ? Math.min(1.0, Math.abs(acceleration) / 4.0) : 0,
    };
  }

  /**
   * Directly set manual acceleration from -1.0 (hard braking/overrun) to +1.0 (full throttle)
   */
  public setManualAcceleration(accel: number): AccelerationResult {
    const clamped = Math.max(-1.0, Math.min(1.0, accel));
    this.smoothedAcceleration = clamped;

    if (clamped >= 0) {
      this.smoothedEffort = clamped;
      return {
        acceleration: clamped,
        effort: clamped,
        isBraking: false,
        engineBrakingIntensity: 0,
      };
    } else {
      this.smoothedEffort = 0;
      const isBraking = clamped < -0.05;
      return {
        acceleration: clamped,
        effort: 0,
        isBraking,
        engineBrakingIntensity: isBraking ? Math.abs(clamped) : 0,
      };
    }
  }

  public reset(initialSpeedKmh: number = 0): void {
    this.lastSpeedKmh = initialSpeedKmh;
    this.lastTimestamp = performance.now();
    this.smoothedAcceleration = 0;
    this.smoothedEffort = 0;
  }
}
